import { getMomentOfInertia } from "@/libs/calculations/equipmentString";
import type { SystemStressResult, SystemStressParams } from "./types";
import { YOUNGS_MODULUS_PSI } from "./constants";
import { calculateStressMultiplier } from "./stringItems";

const ZERO_STRESS: SystemStressResult = {
  bendRadiusFt: 0,
  momentOfInertia: 0,
  n: 0,
  phi: 0,
  stressMultiplier: 0,
  bendingStressPsi: 0,
  axialStressPsi: 0,
  totalBendingStressPsi: 0,
  deflectionInches: 0,
  equipmentDlsDeg100Ft: 0
};

/**
 * Columns `I`, `M`, `N`, `P`, `AB`, `AD`, `AE`, `AG`, `AH` and `AI` for one row.
 *
 * A straight hole gives `F = 0`. The sheet wraps `I` and `AD` in
 * `IFERROR(...,0)`, so both are 0 there, `AG = |AE|` and `AH = 0`.
 */
export function calculateSystemStress(
  params: SystemStressParams
): SystemStressResult {
  const {
    dogLegSeverityDeg100Ft,
    jointLengthFt,
    outerRadiusInches,
    innerRadiusInches,
    axialLoadLb,
    standoffInches
  } = params;

  const momentOfInertia = getMomentOfInertia(
    outerRadiusInches,
    innerRadiusInches
  );

  if (jointLengthFt === 0 || momentOfInertia === 0 || axialLoadLb <= 0) {
    return { ...ZERO_STRESS, momentOfInertia };
  }

  // I - the bend radius. 18000/PI is 100 ft per degree converted to a radius;
  // the sheet leaves the result in feet and never converts it to inches.
  // IFERROR(...,0): a straight hole (F = 0) gives 0, not #DIV/0!.
  const bendRadiusFt =
    dogLegSeverityDeg100Ft === 0
      ? 0
      : 18_000 / (Math.PI * dogLegSeverityDeg100Ft);

  // N and P
  const n = Math.sqrt(axialLoadLb / (YOUNGS_MODULUS_PSI * momentOfInertia));
  const phi = (jointLengthFt / 2) * n;

  // The group that recurs through R..W
  const contactRatio = (2 * n * standoffInches * bendRadiusFt) / jointLengthFt;

  const stressMultiplier = calculateStressMultiplier(phi, contactRatio);

  // AD - see the unit note in the file header. IFERROR(...,0) when I = 0.
  const bendingStressPsi =
    bendRadiusFt === 0
      ? 0
      : YOUNGS_MODULUS_PSI *
        (outerRadiusInches / bendRadiusFt) *
        stressMultiplier;

  // AE
  const area =
    Math.PI * (Math.pow(outerRadiusInches, 2) - Math.pow(innerRadiusInches, 2));
  const axialStressPsi = area === 0 ? 0 : axialLoadLb / area;

  // AG. The sheet takes SQRT of a square, so this is an absolute value.
  const totalBendingStressPsi = Math.abs(axialStressPsi + bendingStressPsi);

  // AH
  const deflectionInches =
    (bendingStressPsi / YOUNGS_MODULUS_PSI) * -dogLegSeverityDeg100Ft;

  // AI. Young's modulus cancels, so this is the dogleg the equipment sees.
  const equipmentDlsDeg100Ft =
    totalBendingStressPsi === 0
      ? 0
      : Math.abs(
          deflectionInches * (-YOUNGS_MODULUS_PSI / totalBendingStressPsi)
        );

  return {
    bendRadiusFt,
    momentOfInertia,
    n,
    phi,
    stressMultiplier,
    bendingStressPsi,
    axialStressPsi,
    totalBendingStressPsi,
    deflectionInches,
    equipmentDlsDeg100Ft
  };
}
